import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useMyDevices } from "@/hooks/useMyDevices";
import { useMyProfile } from "@/hooks/useMyProfile";
import { useMyAccountModal, type MyAccountTab } from "@/hooks/useMyAccountModal";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import { MAX_DEVICES } from "@/domain/session";
import { Avatar } from "./Avatar";
import { ProfileTab } from "./ProfileTab";
import { NotifyTab } from "./NotifyTab";
import { HomeTab } from "./HomeTab";
import { DevicesTab } from "./DevicesTab";
import "./account.css";

/**
 * MyAccountModal — 계정 메뉴 › 내 계정·환경(12 설계 §8-D).
 *  PC·fold: 사이드탭 모달(베이스 `.modal` — 720 고정 + 220 레일이 사이드탭 용도와 맞음, AGENTS §8)
 *  MO(≤659): 전체 시트. 루트 목록(D-4) → 각 탭이 하위 시트(D-5), 하단 탭은 가려짐.
 * 열기·닫기는 useMyAccountModal(어디서든 openMyAccount()). Layout 이 한 번만 그린다.
 */
const TABS: { key: MyAccountTab; label: string; icon: string }[] = [
  { key: "profile", label: "회원정보 관리", icon: "users" },
  { key: "notify", label: "알림 수신 설정", icon: "bell" },
  { key: "home", label: "첫 화면", icon: "home" },
  { key: "devices", label: "기기관리", icon: "monitor" },
];

export function MyAccountModal() {
  const { open, tab, closeMyAccount, setTab } = useMyAccountModal();
  const { user } = useAuth();
  const isMo = useMediaQuery("(max-width: 659px)");
  const profile = useMyProfile({ withAccount: open });
  const { devices, loading: devLoading, error: devError } = useMyDevices(open);
  const dismiss = useBackdropDismiss(closeMyAccount);

  // 로그아웃되면 닫기
  useEffect(() => {
    if (!user && open) closeMyAccount();
  }, [user, open, closeMyAccount]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // MO 하위 시트면 한 단계만 뒤로
      if (isMo && tab) setTab(null);
      else closeMyAccount();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, isMo, tab, setTab, closeMyAccount]);

  if (!open || !user) return null;

  // 불러오기 전·실패 시 "0/3" 처럼 틀린 수를 보이지 않는다
  const deviceCount = devLoading || devError ? "" : user.isAdmin ? `${devices.length}` : `${devices.length}/${MAX_DEVICES}`;
  const active: MyAccountTab = tab ?? "profile";
  const body =
    active === "profile" ? (
      <ProfileTab profile={profile} />
    ) : active === "notify" ? (
      <NotifyTab />
    ) : active === "home" ? (
      <HomeTab />
    ) : (
      <DevicesTab />
    );

  if (isMo) {
    const cur = tab ? TABS.find((t) => t.key === tab) : null;
    return createPortal(
      <div className="modal-backdrop ma-mo-backdrop">
        <div className="ma-mo" role="dialog" aria-modal="true" aria-label={cur ? cur.label : "내 계정·환경"}>
          <div className="ma-mo-hd">
            {cur ? (
              <button type="button" className="ma-icon-btn is-lg" onClick={() => setTab(null)} aria-label="뒤로">
                <Icon name="back" size={20} />
              </button>
            ) : (
              <span className="ma-mo-hd-spacer" />
            )}
            <div className="ma-mo-title">{cur ? cur.label : "내 계정·환경"}</div>
            <button type="button" className="ma-icon-btn is-lg" onClick={closeMyAccount} aria-label="닫기">
              <Icon name="close" size={20} />
            </button>
          </div>
          <div className="ma-mo-body">
            {cur ? (
              body
            ) : (
              <>
                <div className="ma-mo-who">
                  <Avatar className="ma-av is-xl" photo={profile.photo} name={user.name} loginId={user.loginId} />
                  <div>
                    <div className="ma-who-name">{user.name || "사용자"}</div>
                    <div className="ma-help">{profile.affiliation}</div>
                  </div>
                </div>
                {TABS.map((t) => (
                  <button key={t.key} type="button" className="ma-card ma-mo-row" onClick={() => setTab(t.key)}>
                    <span className="ma-mo-row-text">
                      <span className="ma-mo-row-label">{t.label}</span>
                      <span className="ma-help">
                        {t.key === "profile"
                          ? "사진·연락처·비밀번호"
                          : t.key === "notify"
                            ? "앱 푸시·알림 종류"
                            : t.key === "home"
                              ? "로그인 후 가는 화면"
                              : deviceCount || "기기·접속 기록"}
                      </span>
                    </span>
                    <Icon name="chevR" size={18} />
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="modal-backdrop" {...dismiss}>
      <div className="modal ma-modal" role="dialog" aria-modal="true" aria-label="내 계정·환경" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <div className="modal-title">{TABS.find((t) => t.key === active)?.label}</div>
          <button type="button" className="modal-close" onClick={closeMyAccount} aria-label="닫기">
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="modal-side ma-side">
            <div className="ma-side-title">내 계정·환경</div>
            <div className="ma-side-who">
              <Avatar className="ma-av is-md" photo={profile.photo} name={user.name} loginId={user.loginId} />
              <div className="ma-side-who-text">
                <div className="ma-who-name">{user.name || "사용자"}</div>
                <div className="ma-help">{profile.affiliation}</div>
              </div>
            </div>
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`mod-tab ${active === t.key ? "is-active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                <Icon name={t.icon} size={16} />
                <span className="ma-tab-label">{t.label}</span>
                {t.key === "devices" && deviceCount && <span className="ma-tab-count">{deviceCount}</span>}
              </button>
            ))}
          </div>
          <div className="modal-content">{body}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
